import { create } from "zustand";

/**
 * Log levels for filtering and categorization
 */
export type LogLevel = "all" | "info" | "warn" | "error" | "debug" | "trace";

/**
 * Log entry structure
 */
export interface LogEntry {
	id: string;
	timestamp: Date;
	level: Exclude<LogLevel, "all">;
	message: string;
	metadata?: Record<string, unknown>;
}

/**
 * Logs store state and actions
 */
interface LogsStore {
	logs: LogEntry[];
	addLog: (
		level: Exclude<LogLevel, "all">,
		message: string,
		metadata?: Record<string, unknown>
	) => void;
	clearLogs: () => void;
	getFilteredLogs: (level: LogLevel) => LogEntry[];
	exportLogsToFile: (logs: LogEntry[]) => void;
}

/**
 * Zustand store for managing frontend application logs
 */
export const useLogsStore = create<LogsStore>()((set, get) => ({
	addLog: (level, message, metadata) => {
		const newLog: LogEntry = {
			id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
			level,
			message,
			metadata,
			timestamp: new Date(),
		};

		set((state) => ({
			logs: [...state.logs, newLog],
		}));
	},

	clearLogs: () => {
		set({ logs: [] });
	},

	exportLogsToFile: (logs) => {
		const logText = logs
			.map((log) => {
				const timestamp = log.timestamp.toISOString();
				const metadataStr = log.metadata
					? `\n  Metadata: ${JSON.stringify(log.metadata, null, 2)}`
					: "";
				return `[${timestamp}] [${log.level.toUpperCase()}] ${log.message}${metadataStr}`;
			})
			.join("\n\n");

		const blob = new Blob([logText], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `logs-${new Date().toISOString().split("T")[0]}.txt`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	},

	getFilteredLogs: (level) => {
		const { logs } = get();
		if (level === "all") {
			return logs.filter(
				(log) => log.level !== "debug" && log.level !== "trace"
			);
		}
		return logs.filter((log) => log.level === level);
	},

	logs: [],
}));

/**
 * Character limit threshold for exporting logs (100KB)
 */
const EXPORT_THRESHOLD = 100 * 1024;

/**
 * Helper function to check if logs exceed export threshold
 */
export const shouldExportLogs = (logs: LogEntry[]): boolean => {
	const logText = logs
		.map((log) => {
			const timestamp = log.timestamp.toISOString();
			const metadataStr = log.metadata
				? `\n  Metadata: ${JSON.stringify(log.metadata, null, 2)}`
				: "";
			return `[${timestamp}] [${log.level.toUpperCase()}] ${log.message}${metadataStr}`;
		})
		.join("\n\n");

	return logText.length > EXPORT_THRESHOLD;
};

export default useLogsStore;
export type { LogsStore };
